import { useState } from 'react';
import { T } from '@design/index';
import { patientApi } from '../api';

const supported = [
  { label: 'Provider QR', desc: 'Doctor, clinic, hospital' },
  { label: 'Prescription QR', desc: 'Medicine prescriptions' },
  { label: 'Lab Sample Barcode', desc: 'Lab tests and samples' },
  { label: 'Pharmacy Medicine QR', desc: 'Medicine packaging' },
  { label: 'Document QR', desc: 'Reports, certificates, invoices' },
  { label: 'Patient NeoHealthCard QR', desc: 'Verify another patient' },
];

export default function PatientQRScanner() {
  const [manualCode, setManualCode] = useState('');
  const [lookupResult, setLookupResult] = useState('');
  const [looking, setLooking] = useState(false);

  const handleLookup = async () => {
    const code = manualCode.trim();
    if (!code) return;
    setLooking(true);
    setLookupResult('');
    try {
      const result = await patientApi.verifyCard(code);
      if (result.valid && result.patient) {
        setLookupResult(
          `✓ Verified: ${result.patient.fullName} (${result.patient.nhcId})${result.patient.verified ? '' : ' — pending verification'}`,
        );
      } else {
        setLookupResult(`No NeoHealthCard found for "${code}".`);
      }
    } catch (e: any) {
      setLookupResult(e.message || 'Could not verify — please check your connection and try again.');
    } finally {
      setLooking(false);
      setManualCode('');
    }
  };

  return (
    <div style={{ maxWidth: 440, margin: '0 auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Camera frame placeholder */}
      <div style={{
        position: 'relative', borderRadius: 18,
        background: 'linear-gradient(160deg, #0B2545, #001F42)',
        height: 220, overflow: 'hidden', display: 'grid', placeItems: 'center',
      }}>
        {([['top', 'left'], ['top', 'right'], ['bottom', 'left'], ['bottom', 'right']] as const).map(([v, h], i) => {
          const br = `3px solid ${T.teal}`;
          return (
            <div key={i} style={{
              position: 'absolute',
              top: v === 'top' ? 28 : undefined, bottom: v === 'bottom' ? 28 : undefined,
              left: h === 'left' ? 28 : undefined, right: h === 'right' ? 28 : undefined,
              width: 28, height: 28,
              borderTop: v === 'top' ? br : undefined, borderBottom: v === 'bottom' ? br : undefined,
              borderLeft: h === 'left' ? br : undefined, borderRight: h === 'right' ? br : undefined,
              borderTopLeftRadius: v === 'top' && h === 'left' ? 8 : 0,
              borderTopRightRadius: v === 'top' && h === 'right' ? 8 : 0,
              borderBottomLeftRadius: v === 'bottom' && h === 'left' ? 8 : 0,
              borderBottomRightRadius: v === 'bottom' && h === 'right' ? 8 : 0,
            }} />
          );
        })}
        <div style={{
          position: 'absolute', left: '12%', right: '12%', top: '50%',
          height: 2, background: T.teal, boxShadow: `0 0 10px ${T.teal}`,
          animation: 'nhcScan 2s ease-in-out infinite alternate',
        }} />
        <style>{`@keyframes nhcScan{0%{top:14%}100%{top:82%}}`}</style>
        <div style={{
          position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center',
          fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.85)', fontFamily: T.font,
        }}>Point camera at a QR code</div>
      </div>

      <div style={{ fontSize: 10, color: T.textSec, fontFamily: T.font, textAlign: 'center' }}>
        Align the QR code inside the frame to scan automatically.
      </div>

      {/* Supported scan types */}
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 16, padding: 14, boxShadow: T.shadow }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 10 }}>Supported QR Types</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {supported.map((s) => (
            <span key={s.label} style={{
              fontSize: 9, fontWeight: 600, color: T.navy,
              background: `${T.navy}08`, border: `1px solid ${T.navy}18`,
              padding: '5px 10px', borderRadius: 16, fontFamily: T.font,
            }}>{s.label}</span>
          ))}
        </div>
      </div>

      {/* Manual entry */}
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 16, padding: 14, boxShadow: T.shadow }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 8 }}>Manual Code Entry</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            type="text"
            placeholder="Enter NHC ID or verification code..."
            value={manualCode}
            onChange={e => setManualCode(e.target.value)}
            style={{
              flex: 1, padding: '10px 14px', background: T.ice, borderRadius: 10, border: `1px solid ${T.border}`,
              fontSize: 11, fontFamily: T.font, color: T.text, outline: 'none',
            }}
          />
          <button
            disabled={looking}
            onClick={handleLookup}
            style={{
              padding: '10px 16px', background: T.teal, color: '#FFF', border: 'none',
              borderRadius: 10, fontSize: 10, fontWeight: 700, fontFamily: T.font,
              cursor: looking ? 'wait' : 'pointer', whiteSpace: 'nowrap', opacity: looking ? 0.7 : 1,
            }}
          >{looking ? 'Looking up…' : 'Lookup'}</button>
        </div>
        {lookupResult && (
          <div style={{
            marginTop: 8, padding: '8px 12px', background: `${T.teal}08`, border: `1px solid ${T.teal}20`,
            borderRadius: 8, fontSize: 9, color: T.textSec, fontFamily: T.font, lineHeight: 1.5,
          }}>
            {lookupResult}
          </div>
        )}
      </div>

      {/* Note */}
      <div style={{
        background: `${T.navy}06`, border: `1px solid ${T.navy}12`, borderRadius: 12,
        padding: 12, fontSize: 9, color: T.textSec, fontFamily: T.font, lineHeight: 1.6, textAlign: 'center',
      }}>
        Camera scanner integration pending. Scanned results will automatically verify through NeoVerify and link to your records.
      </div>

      {/* Safety Note */}
      <div style={{
        background: `${T.teal}06`, border: `1px solid ${T.teal}15`, borderRadius: 12,
        padding: 12, fontSize: 9, color: T.textSec, fontFamily: T.font, lineHeight: 1.6, textAlign: 'center',
      }}>
        QR/NFC scanning verifies identity only. Medical record access requires patient consent or emergency authorization. All scan and access events are audit logged.
      </div>

      <div style={{ fontSize: 8, color: T.textTer, fontFamily: T.font, textAlign: 'center', lineHeight: 1.5 }}>
        NeoHealthCard™ · Verified scanning · Every access is audit logged
      </div>
    </div>
  );
}
